import { Directive, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, map, Observable } from 'rxjs';
import { RouterService } from '../../services';
import { IActionState } from '../../utils';
import { ErrorOrigin, IRegisterExistingResident } from '../../models';
import * as fromRegistrationStore from '../../+state/account/registration';
import * as fromAccountUser from '../../+state/account/user-profile';

@UntilDestroy()
@Directive()
export abstract class ConsentFormBaseComponent implements OnInit {
  @Output() registrationCompleted = new EventEmitter<boolean>();
  public fg: FormGroup = this.fb.group({
    consent: [false, [Validators.requiredTrue]],
    document: [false],
  });
  public registerResidentActionState$: Observable<IActionState>;
  private email: string;
  private invitationCode: string;

  constructor(
    private fb: FormBuilder,
    private routerService: RouterService,
    private activeRoute: ActivatedRoute,
    private registerStore: Store<fromRegistrationStore.RegistrationState>
  ) {}

  ngOnInit() {
    this.registerStore
      .select(fromRegistrationStore.getRegistrationForm)
      .pipe(untilDestroyed(this))
      .subscribe(regForm => {
        this.email = regForm.email;
        this.invitationCode = regForm.invitationCode;
      });

    this.registerStore
      .select(fromAccountUser.getLegalTexts)
      .pipe(
        map(legalTexts => !!legalTexts?.digitalDocumentsReceivalMandatory),
        distinctUntilChanged(),
        untilDestroyed(this)
      )
      .subscribe(documentConsentMandatory => {
        const documentControl = this.fg.get('document');
        if (documentConsentMandatory) {
          documentControl?.setValidators([Validators.requiredTrue]);
        } else {
          documentControl?.clearValidators();
        }
        documentControl?.updateValueAndValidity();
      });

    this.registerResidentActionState$ = this.registerStore.select(
      fromRegistrationStore.getRegisterResidentActionState
    );
    this.handleRegistrationResponse();
  }

  public submit() {
    if (this.fg.invalid) {
      return;
    }
    const registerResidentInput: IRegisterExistingResident = {
      email: this.email,
      invitationCode: this.invitationCode,
      registrationForExistingUser: true,
      hasConsentedToDigitalDocumentsReceival: this.fg.get('document').value,
    };
    this.registerStore.dispatch(fromRegistrationStore.RegisterResident({ registerResidentInput }));
  }

  private handleRegistrationResponse(): void {
    this.registerResidentActionState$.pipe(untilDestroyed(this)).subscribe(response => {
      if (response.done) {
        this.registrationCompleted.emit(true);
        void this.routerService.navigate(['../register-page4-confirmation-email-info'], {
          relativeTo: this.activeRoute,
        });
      } else if (response.error) {
        this.registrationCompleted.emit(false);
        void this.routerService.navigate(
          [
            '../register-page-registration-error-screen',
            { error: response.error.message, origin: ErrorOrigin.REGISTRATION_CONSENT },
          ],
          {
            relativeTo: this.activeRoute,
          }
        );
      }
    });
  }
}
